import apiClient from '@/services/api_client';

/**
 * Skills imported into the organisation. `/agent-skills`, not `/skills`: the latter is the
 * legacy chat-mode picker. A summary carries no procedure.
 */

/** Never why an import was refused. `broken`: a script the skill needs. `partial`: supporting
 * material the rest of the procedure runs without. */
export type CompatibilityIssue = {
    severity: 'partial' | 'broken';
    message: string;
};

export type AgentSkill = {
    slug: string;
    name: string;
    description: string;
    author_id: string;
    updated_at: string;
    /** Off for this member only, not the organisation. */
    excluded: boolean;
    compatibility_issues: CompatibilityIssue[];
};

export type ImportedSkill = {
    filename: string;
    slug: string;
    /** A skill of that name was already there and has been replaced. */
    replaced: boolean;
    compatibility_issues: CompatibilityIssue[];
};

export type RejectedSkill = {
    filename: string;
    reasons: string[];
};

/** A name the organisation already holds. Held back rather than refused: overwriting loses the
 * previous procedure for good, so the admin has to say they meant it. */
export type SkillConflict = {
    filename: string;
    slug: string;
};

/** One entry per uploaded file: a partial batch is the normal case. */
export type ImportReport = {
    imported: ImportedSkill[];
    rejected: RejectedSkill[];
    conflicts: SkillConflict[];
};

export async function getAgentSkills(tenantId: string): Promise<AgentSkill[]> {
    const response = await apiClient.get(
        `/agent-skills?tenant_id=${encodeURIComponent(tenantId)}`
    );
    return await response.json();
}

/** `overwrite` replaces skills of the same name; without it they come back in `conflicts`. */
export async function importAgentSkills(
    tenantId: string,
    files: File[],
    overwrite = false
): Promise<ImportReport> {
    const formData = new FormData();
    for (const file of files) {
        formData.append('files', file);
    }
    return await apiClient.postFormData(
        `/agent-skills/import?tenant_id=${encodeURIComponent(tenantId)}` +
            `&overwrite=${overwrite}`,
        formData
    );
}

export async function getAgentSkillContent(
    tenantId: string,
    slug: string
): Promise<string> {
    const response = await apiClient.get(
        `/agent-skills/${encodeURIComponent(slug)}/content?tenant_id=${encodeURIComponent(tenantId)}`
    );
    return await response.json();
}

export async function deleteAgentSkill(
    tenantId: string,
    slug: string
): Promise<void> {
    await apiClient.delete(
        `/agent-skills/${encodeURIComponent(slug)}?tenant_id=${encodeURIComponent(tenantId)}`,
        {}
    );
}

/**
 * The skills a message names with `/<name>`, deduplicated, filtered to names the member has.
 * `/` must open a word, or a URL or a date would become a skill request.
 */
export function skillsNamedIn(message: string, names: string[]): string[] {
    const known = new Set(names);
    const found: string[] = [];
    for (const [, candidate] of message.matchAll(/(?:^|\s)\/([a-z0-9-]+)/g)) {
        if (known.has(candidate) && !found.includes(candidate)) {
            found.push(candidate);
        }
    }
    return found;
}

export async function setAgentSkillExcluded(
    tenantId: string,
    slug: string,
    excluded: boolean
): Promise<void> {
    await apiClient.put(
        `/agent-skills/${encodeURIComponent(slug)}/excluded` +
            `?tenant_id=${encodeURIComponent(tenantId)}&excluded=${excluded}`,
        {}
    );
}
